import { CheckCheck, ChevronRight, type LucideIcon } from 'lucide-react';
import { createContext, useContext, useId, type MouseEvent, type ReactNode } from 'react';
import { Link } from 'react-router';
import { toAppError } from '../../api/errors';
import { useMarkSeen } from '../../data/queries';
import type { Item } from '../../domain/models';
import { can, isUntrusted, needsDecision } from '../../domain/models';
import { useT, type Translate } from '../../l10n/i18n';
import { checkStyle, errorMessage, itemKindLabel, statusStyle, timeAgo, workKindCount, workKindIcon, type Tone } from '../core/presentation';
import { Pill } from '../core/widgets/common';
import { useToast } from '../core/widgets/toasts';
import { failedCount, latest, revisedCount, toPublishCount, type WorkEntry } from './workGroups';
import './inbox.css';

// The lines of an employee's work, ported from the mobile app's work_block.dart: one item per line,
// or many of a kind folded into one ("6 žinutės"). Shared by the inbox and an employee's page.

export interface WorkEntriesProps {
  entries: WorkEntry[];
  /** Whether a fold (`key`) shows its items; folds start closed and stay as the person left them. */
  isOpen: (key: string) => boolean;
  toggle: (key: string) => void;
  /** Where a line leads: `/inbox/:id` in the inbox, `/items/:id` elsewhere. */
  itemHref: (id: string) => string;
  /** The item open next to the list, highlighted. */
  selectedId?: string;
  /** Say each item's status, waiting too, for lists that also hold decided work. */
  showStatus?: boolean;
}

/** Lists inside offer "Peržiūrėta" on a report the person can mark seen, the web's counterpart of the
 * mobile swipe. Only the inbox does, as in the mobile app. */
const MarkSeenContext = createContext(false);

export function OfferMarkSeen({ children }: { children: ReactNode }) {
  return <MarkSeenContext.Provider value>{children}</MarkSeenContext.Provider>;
}

export function WorkEntries({ entries, isOpen, toggle, itemHref, selectedId, showStatus = false }: WorkEntriesProps) {
  return (
    <ul className="work-entries">
      {entries.map((e) =>
        e.type === 'single' ? (
          <WorkRow
            key={e.item.id}
            item={e.item}
            href={itemHref(e.item.id)}
            selected={e.item.id === selectedId}
            showStatus={showStatus}
          />
        ) : (
          <Fold
            key={e.key}
            fold={e}
            open={isOpen(e.key)}
            onToggle={() => toggle(e.key)}
            itemHref={itemHref}
            selectedId={selectedId}
            showStatus={showStatus}
          />
        ),
      )}
    </ul>
  );
}

/** What a line says under its title: why a failed task failed, who wants what, or the summary. */
function rowSummary(item: Item): { text?: string; failure: boolean } {
  const failure = item.status === 'failed' ? item.result?.note : undefined;
  if (failure) return { text: failure, failure: true };
  if (item.payload.type === 'access_request') {
    return { text: `${item.payload.requestedBy ?? item.employee.name} · ${item.payload.resource}`, failure: false };
  }
  return { text: item.summary, failure: false };
}

/** Only the flags that matter: urgent, unverified or flagged by the check, revised, and the status once
 * it is not "waiting" (always, with `showStatus`). */
function rowFlags(t: Translate, item: Item, showStatus: boolean): { label: string; tone: Tone }[] {
  const pending = needsDecision(item);
  const flags: { label: string; tone: Tone }[] = [];
  if (item.priority === 'high' && pending) flags.push({ label: t('priorityHigh'), tone: 'danger' });
  // Content from outside: unchecked yet, or what the isolated check found wrong with it (ADR 0011). A
  // message the check passed needs no flag; the warning edge stays until a person lets it in.
  if (isUntrusted(item) && pending) {
    if (!item.check) flags.push({ label: t('untrustedBadge'), tone: 'warning' });
    else if (item.check.verdict !== 'ok') flags.push(checkStyle(t, item.check.verdict));
  }
  if (item.revision > 1 && pending) flags.push({ label: t('revisedBadge'), tone: 'accent' });
  if (!pending || showStatus) flags.push(statusStyle(t, item));
  return flags;
}

/** A drag over a line selects its text instead of navigating when the mouse comes up. */
function keepSelection(e: MouseEvent<HTMLAnchorElement>) {
  const sel = window.getSelection();
  if (sel && !sel.isCollapsed && sel.anchorNode && e.currentTarget.contains(sel.anchorNode)) e.preventDefault();
}

function WorkRow({
  item,
  href,
  selected,
  inFold = false,
  showStatus = false,
}: {
  item: Item;
  href: string;
  selected: boolean;
  inFold?: boolean;
  showStatus?: boolean;
}) {
  const t = useT();
  const titleId = useId();
  const offerSeen = useContext(MarkSeenContext);
  const summary = rowSummary(item);
  const untrusted = isUntrusted(item) && needsDecision(item);
  const seenable = offerSeen && item.type === 'report' && can(item, 'approve');
  const classes = ['work-row', inFold && 'in-fold', selected && 'selected', untrusted && 'untrusted', seenable && 'seenable'];

  return (
    <li className={classes.filter(Boolean).join(' ')}>
      <Link
        to={href}
        className="work-row-link"
        data-item-id={item.id}
        aria-current={selected ? 'true' : undefined}
        draggable={false}
        onClick={keepSelection}
      >
        {!inFold && <KindTile icon={workKindIcon(item.kind)} />}
        <span className="work-row-body">
          <span id={titleId} className="title-small work-row-title">
            {item.title}
          </span>
          {summary.text && <span className={summary.failure ? 'work-row-summary failure' : 'work-row-summary'}>{summary.text}</span>}
          <span className="work-row-meta">
            {!inFold && <span>{itemKindLabel(t, item)}</span>}
            {rowFlags(t, item, showStatus).map((f) => (
              <Pill key={f.label} label={f.label} tone={f.tone} dense />
            ))}
            {/* Last and quiet, so the title keeps the whole width. */}
            <time className="faint" dateTime={item.updatedAt.toISOString()} title={item.updatedAt.toLocaleString()}>
              {timeAgo(t, item.updatedAt)}
            </time>
          </span>
        </span>
      </Link>
      {seenable && <SeenButton item={item} describedBy={titleId} />}
    </li>
  );
}

/** Marks one report seen without opening it; it then leaves the list. */
function SeenButton({ item, describedBy }: { item: Item; describedBy: string }) {
  const t = useT();
  const toast = useToast();
  const markSeen = useMarkSeen();
  const run = async () => {
    try {
      await markSeen.mutateAsync([item]);
      toast(t('decidedSeen'));
    } catch (e) {
      toast(errorMessage(t, toAppError(e)));
    }
  };
  return (
    <button
      type="button"
      className="work-row-seen"
      disabled={markSeen.isPending}
      aria-describedby={describedBy}
      onClick={(e) => {
        e.stopPropagation();
        void run();
      }}
    >
      <CheckCheck size={15} strokeWidth={2.4} aria-hidden />
      {t('actionSeen')}
    </button>
  );
}

type Folded = Extract<WorkEntry, { type: 'folded' }>;

/** The one thing to know about a fold: what waits on the person, what broke, what changed. */
function foldLine(t: Translate, fold: Folded): { text: string; tone?: Tone } {
  const toPublish = toPublishCount(fold.items);
  if (toPublish > 0) {
    const sends = fold.kind === 'message' || fold.kind === 'reply';
    return {
      text: sends ? t('foldToSend', { count: toPublish }) : t('foldToPublish', { count: toPublish }),
      tone: 'warning',
    };
  }
  const failed = failedCount(fold.items);
  if (failed > 0) return { text: t('gistFailed', { count: failed }), tone: 'danger' };
  const revised = revisedCount(fold.items);
  if (revised > 0) return { text: t('foldRevised', { count: revised }), tone: 'accent' };
  return { text: timeAgo(t, latest(fold.items)) };
}

/** Several items of one kind behind a single line; clicking shows them. */
function Fold({
  fold,
  open,
  onToggle,
  itemHref,
  selectedId,
  showStatus,
}: {
  fold: Folded;
  open: boolean;
  onToggle: () => void;
  itemHref: (id: string) => string;
  selectedId?: string;
  showStatus: boolean;
}) {
  const t = useT();
  const listId = useId();
  const line = foldLine(t, fold);
  const urgent = fold.items.some((i) => i.priority === 'high' && needsDecision(i));
  // A closed fold says it holds the item open next to the list.
  const holdsSelected = !open && fold.items.some((i) => i.id === selectedId);

  return (
    <li className={holdsSelected ? 'work-fold holds-selected' : 'work-fold'}>
      <button
        type="button"
        className="work-fold-head"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        data-first-item={fold.items[0]?.id}
        onClick={onToggle}
      >
        <KindTile icon={workKindIcon(fold.kind)} />
        <span className="work-row-body">
          <span className="title-small">{workKindCount(t, fold.kind, fold.items.length, fold.channel)}</span>
          <span className="work-row-meta">
            {urgent && <span className="flag danger">{t('priorityHigh')}</span>}
            <span className={line.tone ? `flag ${line.tone}` : 'faint'}>{line.text}</span>
          </span>
        </span>
        <ChevronRight className="work-fold-chevron" size={20} aria-hidden />
      </button>
      {open && (
        <ul id={listId} className="work-fold-items">
          {fold.items.map((i) => (
            <WorkRow key={i.id} item={i} href={itemHref(i.id)} selected={i.id === selectedId} inFold showStatus={showStatus} />
          ))}
        </ul>
      )}
    </li>
  );
}

function KindTile({ icon: Icon }: { icon: LucideIcon }) {
  return (
    <span className="kind-tile" aria-hidden>
      <Icon size={18} strokeWidth={2} />
    </span>
  );
}
